import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useChapterComments, useAddChapterComment } from '../../api/useComments'
import { useAuthStore } from '../../store/authStore'
import { getErrMsg } from '../../api/client'
import { mediaUrl } from '../../utils/media'
import { formatDate } from '../../utils/date'
import toast from 'react-hot-toast'

export default function ChapterCommentsDrawer({ open, onClose, mangaSlug, chapterId, chapterNumber }) {
  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div
        className="fixed right-0 top-0 bottom-0 z-50 flex flex-col"
        style={{ width: 'min(400px, 100vw)', background: 'var(--reader-surface)', borderLeft: '1px solid var(--reader-border)', boxShadow: '-4px 0 24px rgba(0,0,0,0.5)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--reader-border)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--reader-text)' }}>
            Глава {chapterNumber} — обсуждение
          </span>
          <button onClick={onClose} className="reader-link w-8 h-8 flex items-center justify-center rounded-lg text-lg">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
          <DrawerCommentForm mangaSlug={mangaSlug} chapterId={chapterId} />
          <DrawerCommentsList mangaSlug={mangaSlug} chapterId={chapterId} />
        </div>
      </div>
    </>
  )
}

function DrawerCommentForm({ mangaSlug, chapterId }) {
  const isAuth = useAuthStore((s) => s.isAuth)
  const [text, setText] = useState('')
  const { mutate: addComment, isPending } = useAddChapterComment(mangaSlug, chapterId)

  if (!isAuth) {
    return (
      <div className="p-3 rounded-xl text-sm text-center" style={{ background: 'var(--reader-bg)', color: 'var(--reader-muted)' }}>
        <Link to="/login" className="font-semibold text-accent">Войдите</Link>{' '}
        чтобы оставить комментарий
      </div>
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!text.trim()) return
    addComment({ text: text.trim() }, {
      onSuccess: () => { setText(''); toast.success('Комментарий добавлен') },
      onError: (err) => toast.error(getErrMsg(err, 'Не удалось отправить')),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Напишите комментарий к главе..."
        rows={3}
        className="reader-textarea w-full px-3 py-2.5 rounded-xl text-sm"
      />
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!text.trim() || isPending}
          className="px-4 py-1.5 rounded-lg text-sm font-bold transition-all disabled:opacity-50 bg-accent text-on-accent"
        >
          {isPending ? 'Отправка...' : 'Отправить'}
        </button>
      </div>
    </form>
  )
}

function DrawerCommentsList({ mangaSlug, chapterId }) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useChapterComments(mangaSlug, chapterId)
  const sentinelRef = useRef(null)

  useEffect(() => {
    if (!sentinelRef.current) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasNextPage) fetchNextPage() },
      { threshold: 0.1 }
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, fetchNextPage])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: 'var(--reader-bg)' }} />
        ))}
      </div>
    )
  }

  const allItems = data?.pages.flatMap((p) => p.items) ?? []
  const topLevel = allItems.filter((c) => !c.parent_id)
  const byParent = allItems.reduce((acc, c) => {
    if (c.parent_id) (acc[c.parent_id] ??= []).push(c)
    return acc
  }, {})

  if (topLevel.length === 0) {
    return (
      <p className="text-sm text-center py-8" style={{ color: 'var(--reader-muted)' }}>
        Комментариев пока нет. Будьте первым!
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {topLevel.map((c) => (
        <DrawerCommentItem key={c.id} comment={c} replies={byParent[c.id] || []} />
      ))}
      <div ref={sentinelRef} className="h-1" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <span className="text-xs animate-pulse" style={{ color: 'var(--reader-muted)' }}>Загрузка...</span>
        </div>
      )}
    </div>
  )
}

function DrawerCommentItem({ comment, replies }) {
  const avatarSrc = comment.author.avatar_url ? mediaUrl(comment.author.avatar_url) : '/avatars/1.svg'
  const date = formatDate(comment.created_at)

  return (
    <div>
      <div className="flex gap-2.5">
        <img src={avatarSrc} alt="" className="w-7 h-7 rounded-full shrink-0 object-cover" style={{ background: 'var(--reader-border)' }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold" style={{ color: 'var(--reader-text)' }}>{comment.author.username}</span>
            <span className="text-xs" style={{ color: 'var(--reader-muted)' }}>{date}</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--reader-text)', wordBreak: 'break-word', opacity: 0.85 }}>
            {comment.text}
          </p>
        </div>
      </div>
      {replies.length > 0 && (
        <div className="ml-9 mt-2 flex flex-col gap-2 pl-3" style={{ borderLeft: '2px solid var(--reader-border)' }}>
          {replies.map((r) => <DrawerCommentItem key={r.id} comment={r} replies={[]} />)}
        </div>
      )}
    </div>
  )
}
